import * as rxjs from 'rxjs';
import _ from 'lodash';
import * as Elevated from '@pitaman71/omniglot-introspect';

import Dependencies from '../Dependencies';
import * as Mutations from '../Mutations';
import * as Properties from '../Properties';
import * as Stores from '../Stores';
import * as Streams from '../Streams';

const __moduleName__ = 'omniglot-live-data.Clients.SequenceClient';

export interface State<ValueType> { 
    values: Array<ValueType>, 
    status: Streams.Status
};

export abstract class SequenceClient<ValueType> {
    abstract assign(newValues: ValueType[]): this;
    abstract clear(): this;
    abstract remove(...values: ValueType[]): this;
    abstract append(after: ValueType|undefined, ... newValues: ValueType[]): this;
    abstract prepend(before: ValueType|undefined, ... newValues: ValueType[]): this;

    static debugMode = false;

    static fromDelta<ValueType, DomainType extends Elevated.Domain<ValueType>>(
        name: string,
        setValues: (delta: (oldValues: Array<ValueType>) => Array<ValueType>) => void,
        setStatus: (delta: (_status: Streams.Status) => Streams.Status) => void
    ) {
        const __className__ = `${__moduleName__}.fromDelta`;
        return new class extends SequenceClient<ValueType> {    
            subscriptions = new Streams.Subscriptions();

            _watch(stream: Streams.SequenceStream<ValueType, DomainType>) {
                this.subscriptions.unsubscribe();
                this.subscriptions = stream.watch(name).mutations({
                    next: (mutation) => {
                        if(mutation.revert) {
                            setValues(_values => []);
                            this._watch(stream);
                        } else {
                            mutation.dispatch(this); 
                        }
                    }, 
                    complete: () => {},
                    error: (error: any) => {
                        console.error(`${__className__} subscription to ${stream.persistKey} receives error ${error}`)
                    }
                });
                return this.subscriptions;
            }

            watchParent(stream: Streams.SequenceStream<ValueType, DomainType>): Streams.Subscriptions {
                return this._watch(stream);
            }
            assign(values_: ValueType[]) {
                setValues(_values => _values === values_ ? _values : values_);
                return this;

            }
            clear() {
                setValues(old => []);
                return this;
            }
            remove(...values_: ValueType[]) {
                setValues(old => old.filter(_value => values_.filter(value_ => _.isEqual(_value, value_)).length === 0));
                return this;
            }
            append(after: ValueType|undefined, ...newValues: ValueType[]) {
                setValues(_values => {
                    const values_ = [
                        ..._values.reduce((result: ValueType[], item: ValueType) => 
                            _.isEqual(item, after) ? [ ...result, item, ...newValues ] : [ ...result, item ]
                        , [] ),
                        ...after === undefined ? newValues : []
                    ];
                    return values_;
                });
                return this;
            }
            prepend(before: ValueType|undefined, ...newValues: ValueType[]) {
                setValues(old => [
                    ...before === undefined ? newValues : [],
                    ...old.reduce((result: ValueType[], item: ValueType) => 
                        _.isEqual(item, before) ? [ ...result, ...newValues, item ] : [ ...result, item ]
                    , [] )
                ]);
                return this;
            }
        }();
    }
    static Eager<ValueType>(name_: string) { 
        return new class _Variable {
            _name: string;
            _current: State<ValueType>;
            _downstream: rxjs.Subject<State<ValueType>>;
            _client?: SequenceClient<ValueType>;

            constructor(name: string) {
                this._name = name;
                this._current = { values: [], status: {} };
                this._downstream = Dependencies.makeSubject();
                this._client = undefined;
            }

            value() { return [...this._current.values]; }
            status() { return this._current.status; }
            authorize(status: boolean) {
                this._current.status.authorized = status;
                if(!status) this.set([]);
            }
            set(values_: Array<ValueType>, status_?: Streams.Status) { 
                if(SequenceClient.debugMode)
                    console.log(`Eager CHANGE ${this._name} ${this._current.values} => ${values_}`);
                this._current.values = [...values_];
                if(status_ !== undefined) this._current.status = status_;
                this._downstream.next({...this._current});
            }
            error(err: any) {
                this._downstream.error(err);
            }
            complete() {
                this._downstream.complete();
            }

            upstream(
                stream_: Streams.SequenceStream<ValueType>
            ): _Variable {
                const client = SequenceClient.fromDelta<ValueType, Elevated.Domain<ValueType>>(this._name, delta => {
                    const values_ = delta(this._current.values)
                    this.set(values_);
                }, delta => {
                    const status_ = delta(this._current.status);
                    this._current.status = status_;
                });
                const subscription = client.watchParent(stream_);
                this._client = stream_.stateful();
                return this;
            }
            watch(observer: rxjs.Observer<State<ValueType>>) {
                return this._downstream.subscribe(observer);
            }
            change(): undefined|SequenceClient<ValueType> {
                return this._client;
            }
            stream(domain: Elevated.Domain<ValueType>) {
                const eager = this;
                return new class implements Streams.SequenceStream<ValueType> {
                    persistKey = Stores.Key.fromEmpty();
                    peerId: string = Elevated.factories.makeObjectId();
                    domain = domain;
                    watch(peerId: string) { 
                        return new class {
                            mutations(observer: rxjs.Observer<Mutations.OfSequence.Mutation<ValueType>>) { 
                                let prev: null|Mutations.OfSequence.Mutation<ValueType> = null;
                                const send = (values_: Array<ValueType>) => {
                                    if(values_.length) {
                                        const mutation_ = Mutations.OfSequence.Mutation.fromClear(prev);
                                        observer.next(mutation_);
                                        prev = mutation_;
                                    } else {
                                        const mutation_ = Mutations.OfSequence.Mutation.fromAssign(prev, ...values_);
                                        observer.next(mutation_);
                                        prev = mutation_;
                                    }
                                };
                                send(eager.value());
                                const subscriptions = new Streams.Subscriptions();
                                subscriptions.add(eager.watch({
                                    next: state_ => send(state_.values),
                                    error: err => {},
                                    complete: () => {}
                                }));
                                return subscriptions;
                            }
                        }
                    }
                    stateful() { return undefined }
                    persistent() { return undefined }        
                }();        
            }
        }(name_);
    }
}

